import React from "react";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react";
import { AppLayout } from "../shared/layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowRight,
  DollarSign,
  Activity,
  TrendingUp,
  Receipt,
  CreditCard,
} from "lucide-react";
import { getTasks } from "../../../data/requests/task-requests";
import { getDashboardStats, getMyPayments } from "../../../data/requests/dashboard-requests";
import { formatTRY } from "@/utils/currency";

// Dashboard Stats Components
const StatsCard = ({
  title,
  value,
  icon: Icon,
  trend,
  trendValue,
  color = "blue",
  linkTo,
  linkText = "Tümünü Gör",
}) => {
  const colorClasses = {
    blue: "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30",
    green: "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30",
    orange: "border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/30",
    purple: "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/30",
  };

  const iconColorClasses = {
    blue: "text-blue-600",
    green: "text-green-600",
    orange: "text-orange-600",
    purple: "text-purple-600",
  };

  return (
    <Card className={`${colorClasses[color]}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold text-foreground">{value}</p>
            {trend && (
              <p
                className={`text-sm ${
                  trend === "up" ? "text-green-600" : "text-red-600"
                } flex items-center mt-1`}
              >
                <TrendingUp className="w-4 h-4 mr-1" />
                {trendValue}
              </p>
            )}
          </div>
          <Icon className={`w-8 h-8 ${iconColorClasses[color]}`} />
        </div>
        {linkTo && (
          <Link to={linkTo} className="block mt-3">
            <Button variant="outline" size="sm" className="w-full gap-1 text-xs">
              {linkText}
              <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

// Task List Component
const TaskItem = ({ task, type }) => {
  const priorityColors = {
    Acil: "destructive",
    Normal: "secondary",
    Düşük: "outline",
  };

  const statusIcons = {
    Açık: Clock,
    "Devam Ediyor": Clock,
    Tamamlandı: CheckCircle,
    İptal: AlertCircle,
  };

  const StatusIcon = statusIcons[task.status] || Clock;
  
  // Çoklu kullanıcı desteği için assignedUsers kullan
  const assignedUsers = task.assignedUsers || [];
  
  const renderAssignedUsers = () => {
    if (assignedUsers.length === 0) return null;
    if (assignedUsers.length === 1) {
      return (
        <p className="text-xs text-purple-600">
          → {assignedUsers[0].name}
        </p>
      );
    }
    return (
      <p className="text-xs text-purple-600">
        → {assignedUsers.length} kişi
      </p>
    );
  };

  return (
    <Card
      className={`mb-2 hover:shadow-md transition-shadow ${
        type === "created"
          ? "border-l-4 border-l-purple-400"
          : "border-l-4 border-l-blue-400"
      }`}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <StatusIcon
                className={`w-3.5 h-3.5 ${
                  task.status === "Tamamlandı"
                    ? "text-green-600"
                    : task.priority === "Acil"
                    ? "text-red-600"
                    : "text-yellow-600"
                }`}
              />
              <h4 className="font-semibold text-sm text-foreground line-clamp-1">
                {task.title}
              </h4>
              <Badge
                variant={priorityColors[task.priority]}
                className="text-xs"
              >
                {task.priority}
              </Badge>
            </div>
            {task.description && (
              <p className="text-xs text-muted-foreground mb-1 line-clamp-2">
                {task.description}
              </p>
            )}
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">
                {task.status}
              </Badge>
              {type === "assigned" && task.createdBy && (
                <p className="text-xs text-blue-600">{task.createdBy.name}</p>
              )}
              {type === "created" && renderAssignedUsers()}
            </div>
          </div>
          <Link to={`/app/tasks/${task.id}`}>
            <Button variant="ghost" size="sm" className="ml-2 h-8">
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};


export const MyDashboardScreen = observer(() => {
  // Bana atanan görevleri çek
  const { data: assignedTasksData } = useQuery({
    queryKey: ["tasks", "assigned"],
    queryFn: () => getTasks("assigned").then((res) => res.data),
  });

  // Benim açtığım görevleri çek
  const { data: createdTasksData } = useQuery({
    queryKey: ["tasks", "created"],
    queryFn: () => getTasks("created").then((res) => res.data),
  });

  // Tamamlanmış görevleri filtrele
  const assignedTasks = (assignedTasksData || []).filter(
    (t) => t.status !== "Tamamlandı" && t.status !== "İptal"
  );
  const createdTasks = (createdTasksData || []).filter(
    (t) => t.status !== "Tamamlandı" && t.status !== "İptal"
  );

  // Fetch user-specific dashboard statistics
  const { data: userStatsData } = useQuery({
    queryKey: ["user-dashboard-stats"],
    queryFn: () => getDashboardStats().then((res) => res.data),
    staleTime: 5 * 60 * 1000, // 5 dakika cache
  });

  // Fetch user's payments
  const { data: myPaymentsData } = useQuery({
    queryKey: ["my-payments"],
    queryFn: () => getMyPayments().then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });

  const userStats = userStatsData?.statistics || {};

  return (
    <AppLayout title="Panel">
      <div className="space-y-6">
        {/* Ödemelerim Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Ödemelerim
                </CardTitle>
                <CardDescription>
                  Size ait payment'ların özet istatistikleri
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(!myPaymentsData || myPaymentsData.payments?.length === 0) ? (
              <div className="text-center py-8 text-muted-foreground">
                <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">Henüz size ait payment bulunmuyor.</p>
                <p className="text-xs mt-1">Payment oluştururken "Related User" alanına adınızı yazın.</p>
              </div>
            ) : (
              <>
              {/* Overall Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <StatsCard
                  title="Toplam Ödeme"
                  value={myPaymentsData.overallStats?.totalPayments || 0}
                  icon={Receipt}
                  color="blue"
                  linkTo="/my-payments"
                />
                <StatsCard
                  title="Toplam Komisyon"
                  value={myPaymentsData.overallStats?.totalCommissions || 0}
                  icon={Users}
                  color="purple"
                  linkTo="/my-payments-commissions"
                />
                <StatsCard
                  title="Fatura Yüklendi"
                  value={myPaymentsData.overallStats?.totalDetails || 0}
                  icon={FileText}
                  color="orange"
                  linkTo="/my-payments-commission-details"
                />
              </div>

              {/* Status Summary */}
              {myPaymentsData.overallStats?.statusSummary &&
                Object.keys(myPaymentsData.overallStats.statusSummary).length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Durum Özeti
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {Object.entries(myPaymentsData.overallStats.statusSummary).map(
                        ([status, count]) => (
                          <div
                            key={status}
                            className="bg-muted/50 p-3 rounded-lg border"
                          >
                            <p className="text-xs text-muted-foreground mb-1 truncate" title={status}>
                              {status}
                            </p>
                            <p className="text-xl font-bold text-foreground">
                              {count}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* Amount Summary by Status */}
              {myPaymentsData.overallStats?.totalAmountSummary &&
                Object.keys(myPaymentsData.overallStats.totalAmountSummary).length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Durumlara Göre Toplam Tutarlar
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {Object.entries(myPaymentsData.overallStats.totalAmountSummary).map(
                        ([status, amount]) => (
                          <div
                            key={status}
                            className="bg-green-50 dark:bg-green-950/30 p-3 rounded-lg border border-green-200 dark:border-green-800"
                          >
                            <p className="text-xs text-muted-foreground mb-1 truncate" title={status}>
                              {status}
                            </p>
                            <p className="text-lg font-bold text-green-700 dark:text-green-400">
                              {formatTRY(amount)}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              </>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sol Taraf - Benim Müşterilerim */}
        <div className="space-y-4">
          {userStatsData && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Benim Müşterilerim - {userStatsData.userName}
                    </CardTitle>
                    <CardDescription>
                      Sizin yönettiğiniz müşterilere ait istatistikler
                    </CardDescription>
                  </div>
                  <Link to="/app/reports">
                    <Button variant="outline" className="gap-2">
                      Detaylı Rapor
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatsCard
                    title="Toplam Müşteri"
                    value={userStats.totalClients || 0}
                    icon={Users}
                    color="blue"
                  />
                  <StatsCard
                    title="Banka Bilgisi Eksik"
                    value={userStats.clientsWithoutBankInfo || 0}
                    icon={AlertCircle}
                    color="orange"
                  />
                  <StatsCard
                    title="Fatura Yüklendi"
                    value={userStats.commissionDetailsWithInvoice || 0}
                    icon={FileText}
                    color="purple"
                  />
                </div>

                {/* Last Month Accounting Summary */}
                {userStats.lastMonthAccountingTotals &&
                  Object.keys(userStats.lastMonthAccountingTotals).length >
                    0 && (
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Geçen Ay Accounting Ödemeleri
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
                        {Object.entries(
                          userStats.lastMonthAccountingTotals
                        ).map(([currency, amount]) => (
                          <div
                            key={currency}
                            className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-lg border border-blue-200 dark:border-blue-800"
                          >
                            <p className="text-xs text-muted-foreground mb-1">
                              {currency}
                            </p>
                            <p className="text-xl font-bold text-blue-700 dark:text-blue-400">
                              {Number(amount).toLocaleString("tr-TR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sağ Taraf - Görevler */}
        <div className="space-y-4">
          {/* Bana Atanan Görevler */}
          <Card>
            <CardHeader>
              <Link
                to="/app/tasks?filter=assigned"
                className="hover:opacity-80"
              >
                <CardTitle className="flex items-center justify-between cursor-pointer">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Bana Atanan Görevler
                  </span>
                  <Badge variant="secondary">{assignedTasks.length}</Badge>
                </CardTitle>
              </Link>
              <CardDescription>Size atanan görevler</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {assignedTasks.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    <p>Henüz size atanmış görev bulunmuyor</p>
                  </div>
                ) : (
                  assignedTasks
                    .slice(0, 3)
                    .map((task) => (
                      <TaskItem key={task.id} task={task} type="assigned" />
                    ))
                )}
                {assignedTasks.length > 3 && (
                  <Link to="/app/tasks?filter=assigned">
                    <Button variant="outline" className="w-full mt-2">
                      Tümünü Gör ({assignedTasks.length})
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Benim Açtığım Görevler */}
          <Card>
            <CardHeader>
              <Link to="/app/tasks?filter=created" className="hover:opacity-80">
                <CardTitle className="flex items-center justify-between cursor-pointer">
                  <span className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Açtığım Görevler
                  </span>
                  <Badge variant="outline">{createdTasks.length}</Badge>
                </CardTitle>
              </Link>
              <CardDescription>Başkalarına atadığınız görevler</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {createdTasks.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    <p>Henüz görev oluşturmadınız</p>
                  </div>
                ) : (
                  createdTasks
                    .slice(0, 3)
                    .map((task) => (
                      <TaskItem key={task.id} task={task} type="created" />
                    ))
                )}
                {createdTasks.length > 3 && (
                  <Link to="/app/tasks?filter=created">
                    <Button variant="outline" className="w-full mt-2">
                      Tümünü Gör ({createdTasks.length})
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    </AppLayout>
  );
});
